import React from 'react';
import { createPortal } from 'react-dom';
import { Lock, X } from 'lucide-react';
import SmartImage from './SmartImage';
import { resolveImageUrl as resolveImageUrlUtil } from '../utils/urlResolver';
import { enrichMuseumShare } from '../utils/museumShareAccess';

function ContributionCard({ share, isUnlocked, onSelect }) {
  const enriched = enrichMuseumShare(share) || share;
  const cover = resolveImageUrlUtil(enriched.coverUrl) || enriched.coverUrl;

  return (
    <button
      type="button"
      onClick={() => onSelect(enriched)}
      className="group relative overflow-hidden rounded-2xl border border-[rgba(245,203,92,0.42)] bg-[#FFFDF9] text-left shadow-[0_8px_24px_rgba(120,72,20,0.08)] transition-all duration-300 hover:-translate-y-0.5 hover:border-[#F5CB5C] hover:shadow-[0_14px_32px_rgba(120,72,20,0.12)]"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#f4efe6]">
        {cover ? (
          <div
            className={`h-full w-full overflow-hidden ${isUnlocked ? 'transition-transform duration-300 group-hover:scale-[1.03]' : ''}`}
            style={
              isUnlocked
                ? undefined
                : { filter: 'blur(18px) brightness(0.65) saturate(0.7)', transform: 'scale(1.12)' }
            }
          >
            <SmartImage
              src={cover}
              alt={enriched.title}
              className="h-full w-full object-cover"
              containerClassName="h-full w-full"
            />
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-3xl text-[#c9a574]">🎨</div>
        )}
        {!isUnlocked ? (
          <div className="absolute inset-0 z-[1] flex flex-col items-center justify-center bg-[rgba(47,37,27,0.55)] px-3 text-center backdrop-blur-[2px]">
            <Lock className="mb-1.5 h-6 w-6 text-white" strokeWidth={2} aria-hidden />
            <p className="text-[11px] font-semibold text-white sm:text-xs">
              Join this activity to view
            </p>
          </div>
        ) : null}
        <span className="absolute left-2 top-2 z-[2] rounded-full bg-white/90 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-[#9A7A55]">
          {enriched.sourceLabel}
        </span>
      </div>
      <div className="p-3 sm:p-3.5">
        <p className="line-clamp-2 text-sm font-semibold text-[#2F251B]">{enriched.title}</p>
        <p className="mt-1 text-[11px] text-[#6B5948]">
          {isUnlocked ? 'Tap to explore gallery' : 'Contribute to unlock'}
        </p>
      </div>
    </button>
  );
}

export default function PublicMuseumContributionsModal({
  shares,
  canAccessShare,
  onShareSelect,
  onClose,
}) {
  if (!shares?.length) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[250] flex items-end justify-center bg-black/55 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="museum-contributions-title"
      onClick={onClose}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[28px] border border-[rgba(245,203,92,0.45)] bg-[#FFFDF8] shadow-2xl sm:rounded-[28px]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[rgba(245,203,92,0.28)] px-5 py-4 sm:px-6">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9A7A55]">
              Artist Salon
            </p>
            <h2 id="museum-contributions-title" className="mt-1 text-lg font-bold text-[#2F251B] sm:text-xl">
              Salon contributions
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#e8d4bc] bg-white text-[#5A4632] hover:bg-[#fff7e8]"
            aria-label="Close"
          >
            <X className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {shares.map((share) => {
              const enriched = enrichMuseumShare(share) || share;
              const isUnlocked = canAccessShare ? canAccessShare(enriched) : true;
              return (
                <ContributionCard
                  key={enriched.id || enriched.key}
                  share={enriched}
                  isUnlocked={isUnlocked}
                  onSelect={onShareSelect}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
